// Core packages
import {
  Component, ElementRef, Input, OnDestroy, OnInit, ViewChild
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subject, Subscription } from "rxjs";
import {
  startWith, takeUntil, tap
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertyDetailDocumentNoteDialogComponent } from "src/app/modules/properties/components/document-note-dialog/document-note-dialog.component";
import MappingIndex from "src/app/modules/properties/components/mapping-index.interface";
import { PropertyDetailMappingNoteDialogComponent } from "src/app/modules/properties/components/mapping-note-dialog/mapping-note-dialog.component";
import { CondoStructuresService } from "src/app/modules/properties/condo-structures.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";


@Component({
  selector: "admin-qrcode-mapping",
  templateUrl: "./adminQRCodeMapping.component.html",
  styleUrls: ["./adminQRCodeMapping.component.scss"],
})
export class AdminQRCodeMappingComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  onDestroy$ = new Subject<void>();
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserToleType = "";
  loggedUserMaster = false;
  loggedUserAdmin = false;
  lang = 'it';
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!
  
  cardIsLoading = true;
  mappingIndex: MappingIndex[] = [];
  @ViewChild("card", { static: false }) card: ElementRef;
  @ViewChild("panelContainer", { static: false }) panelContainer: ElementRef<any>;
  formIsLoading = false; // Used to view/hide form loading spinner
  // formItems: any[] = [];
  activeItem: MappingIndex;
  activeSubitem: any;
  // BEGIN - Formly config
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {
    formState: {
      disabled: false
    }
  };
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: true,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Formly config

  property: any;
  condoStructure: any;
  @Input() formActiveSection: string;
  @Input() formActiveSubitemId: string;

  existingNotes: any[] = [];
  enableFormModify = false;
  sectionPlant = false;
  propertyId: string;
  remakePlant = false;
  remakePlantDocs: any = {};
  rimappatura = false;
  mappaturaOpen = false;
  previousFormValue: any;
  showSideMenu = true;

  subscriptionType = 'FREE';

  condoStructureTemplateId = "";
  condoStructureTemplate: any[] = [];

  notesExist = false;

  
  /**
   * Class constructor
   */
  constructor(
    public configService: ConfigService,
    private propertiesService: PropertiesService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private condoStructuresService: CondoStructuresService,
    private settingsService: SettingsService,
  ) {}

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Subscribe to current lang and init component
    this.translateService.use("it");

    const { structureId } = this.activatedRoute.snapshot.params;

    if (structureId && structureId !== '') {
      this.condoStructuresService
      .getView(structureId)
      .subscribe(res => {
        if (res.status) {
          this.condoStructure = res.data;
        } else {
          this.toastrService.error(res.message);
          this.router.navigate([this.lang, "errors", "404"]);
        }
        this.subscriptions.push(
          this.propertiesService.getView(this.condoStructure.property).subscribe(
            res => {
              if (res.status) {
                // Set permission data
                this.property = res.data;

                this.onChanges();
  
                return;
              }
  
              // Something bad happened
              this.router.navigate([this.lang, "errors", "404"]);
            },
            err => {
              // if we came here users with given userId do not exist
              this.router.navigate([this.lang, "errors", "404"]);
            }
          )
        );
  
  
        for (const key in this.condoStructure) {
          if (
            Array.isArray(this.condoStructure[key]) &&
            this.condoStructure[key].length
          ) {
            for (const el of this.condoStructure[key]) {
              if (el.note && Array.isArray(el.note) && el.note.length) {
                this.notesExist = true;
                break;
              }
            }
            if (this.notesExist) {
              break;
            }
          }
        }

        this.cardIsLoading = false;
      });

    } else {
      this.router.navigate([this.lang, "errors", "404"]);
    }
  }

  /**
   * Listen on input values changes
   *
   * @since 1.0.0
   */
  onChanges(): void {
    // Update property
    if (this.property && this.property.currentValue && this.property.currentValue._id) {
      this.property = this.property.currentValue;
      this.propertyId = this.property.currentValue._id;
    }

    // Update form section depending on current mappingFormActiveSection
    if (this.property) {
      this.mappaturaOpen = this.condoStructure.open;
      if (this.condoStructure.rimappatura) {
        this.rimappatura = true;
      }
      if (this.mappingIndex.length === 0) {
        this.subscriptions.push(
          this.settingsService.getStructureView().subscribe(
            (res) => {
              if (res.status) {
                if (
                  res.condoStructureTemplate &&
                  Array.isArray(res.condoStructureTemplate) &&
                  res.condoStructureTemplate.length > 0 &&
                  res.condoStructureTemplate[0].area &&
                  Array.isArray(res.condoStructureTemplate[0].area)
                ) {
                  this.condoStructureTemplateId = res.condoStructureTemplate[0]._id;
                  this.condoStructureTemplate = res.condoStructureTemplate[0].area;

                  const structureTemplate = JSON.parse(JSON.stringify(this.condoStructureTemplate));

                  // hide question deleted
                  this.mappingIndex = structureTemplate.filter((el: any) => {
                    if (!el.deleted) {
                      if (el.content && Array.isArray(el.content)) {
                        const content = el.content.filter((subEl: any) => !subEl.deleted);
                        if (content && Array.isArray(content)) {
                          content.forEach((subContent: any) => {
                            if (
                              subContent && 
                              subContent.formConfig &&
                              Array.isArray(subContent.formConfig) &&
                              subContent.formConfig.length === 1 && 
                              subContent.formConfig[0].fieldGroup &&
                              Array.isArray(subContent.formConfig[0].fieldGroup)
                            ) {
                              const fieldGroup = subContent.formConfig[0].fieldGroup.filter((group: any) => !group.deleted);
                              subContent.formConfig[0].fieldGroup = fieldGroup;
                            }
                          });
                        }
                        el.content = content;
                      }
                      return true;
                    }
                  });

                  this.mappingIndex = this.translateKeys2(
                    ["placeholder", "label"],
                    this.mappingIndex
                  );

                  this.updateFormSection();
                  this.cardIsLoading = false;

                  // Check property type: Condo or BSC
                  this.subscriptionType = this.property.subscription.key;

                  if (this.property.type === "bsc") {
                    const strutture = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.strutture";
                    });
                    if (strutture
                      && strutture.content
                      && Array.isArray(strutture.content)
                    ) {
                      let index = strutture.content.findIndex((item: any) => {
                        return item.key === "condominiBsc"
                      });
                      strutture.content[index].hidden = false;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "fabbricatoAbitativo"
                      });
                      strutture.content[index].hidden = true;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "struttureFabbindip"
                      });
                      strutture.content[index].hidden = false;
                    }

                    const trattamentoAcqua = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.trattamentoAcqua";
                    });
                    if (trattamentoAcqua
                      && trattamentoAcqua.content
                      && Array.isArray(trattamentoAcqua.content)
                    ) {
                      const index = trattamentoAcqua.content.findIndex((item: any) => {
                        return item.key === "sottostazioneAltroCondBsc"
                      });
                      trattamentoAcqua.content[index].hidden = true;
                    }

                    const riscaldamentoCondizionamento = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.riscaldamentoCondizionamento";
                    });
                    if (riscaldamentoCondizionamento
                      && riscaldamentoCondizionamento.content
                      && Array.isArray(riscaldamentoCondizionamento.content)
                    ) {
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "sottostazioniCt"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = true;
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "centraleRicervimento"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = true;
                      const index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                        return item.key === "cannaCollettiva"
                      });
                      riscaldamentoCondizionamento.content[index].hidden = true;
                    }

                    const dispositiviAntincendio = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.dispositiviAntincendio";
                    });
                    if (dispositiviAntincendio
                      && dispositiviAntincendio.content
                      && Array.isArray(dispositiviAntincendio.content)
                    ) {
                      const index = dispositiviAntincendio.content.findIndex((item: any) => {
                        return item.key === "evac"
                      });
                      dispositiviAntincendio.content[index].hidden = true;
                    }
                  } else {
                    const strutture = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.strutture";
                    });
                    if (strutture
                      && strutture.content
                      && Array.isArray(strutture.content)
                    ) {
                      let index = strutture.content.findIndex((item: any) => {
                        return item.key === "condominiBsc"
                      });
                      strutture.content[index].hidden = true;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "fabbricatoAbitativo"
                      });
                      strutture.content[index].hidden = false;
                      index = strutture.content.findIndex((item: any) => {
                        return item.key === "struttureFabbindip"
                      });
                      strutture.content[index].hidden = false;
                    }

                    const trattamentoAcqua = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.trattamentoAcqua";
                    });
                    if (trattamentoAcqua
                      && trattamentoAcqua.content
                      && Array.isArray(trattamentoAcqua.content)
                    ) {
                      const index = trattamentoAcqua.content.findIndex((item: any) => {
                        return item.key === "sottostazioneAltroCondBsc"
                      });
                      trattamentoAcqua.content[index].hidden = false;
                    }

                    const riscaldamentoCondizionamento = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.riscaldamentoCondizionamento";
                    });
                    if (riscaldamentoCondizionamento
                      && riscaldamentoCondizionamento.content
                      && Array.isArray(riscaldamentoCondizionamento.content)
                    ) {
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "sottostazioniCt"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = false;
                      // let index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                      //   return item.key === "centraleRicervimento"
                      // });
                      // riscaldamentoCondizionamento.content[index].hidden = false;
                      const index = riscaldamentoCondizionamento.content.findIndex((item: any) => {
                        return item.key === "cannaCollettiva"
                      });
                      riscaldamentoCondizionamento.content[index].hidden = false;
                    }

                    const dispositiviAntincendio = this.mappingIndex.find((el: any) => {
                      return el.title === "properties.structures.dispositiviAntincendio";
                    });
                    if (dispositiviAntincendio
                      && dispositiviAntincendio.content
                      && Array.isArray(dispositiviAntincendio.content)
                    ) {
                      const index = dispositiviAntincendio.content.findIndex((item: any) => {
                        return item.key === "evac"
                      });
                      dispositiviAntincendio.content[index].hidden = false;
                    }
                  }

                  this.watchFormChanges();
                  this.watchScaleChanges();
                  this.watchBruciatoriChanges();

                  // Enable fields if we're adding a new element, else disable form
                  if (this.form && this.activeItem
                    && (!this.formActiveSubitemId || this.activeItem.key === "anagrafica")
                    && this.mappaturaOpen
                  ) {
                    this.options.formState = {
                      disabled: false
                    };
                    this.formBtnOptions.disabled = false;
                    this.enableFormModify = true;

                    // Check new element and remake plant, then
                    // get save docs to import in new plant
                    if (this.formActiveSection && !this.formActiveSubitemId) {
                      this.remakePlantDocs = this.condoStructuresService.getRemakeStrucutureDocs();
                      // Check if same plant then remake plant.
                      if (this.remakePlantDocs
                        && this.remakePlantDocs.activeSection &&
                        this.remakePlantDocs.activeSection === this.formActiveSection) {
                        this.remakePlant = true;
                      } else {
                        this.remakePlant = false;
                        this.condoStructuresService.setRemakeStrucutureDocs("", "", {});
                      }
                    }
                    if (typeof this.model.files === "undefined") {
                      this.model["files"] = [];
                    }
                  } else if (this.form && this.formActiveSubitemId) {
                    this.options.formState = {
                      disabled: true
                    };
                    this.formBtnOptions.disabled = true;
                    this.enableFormModify = false;
                  }



                }
              }
            })
        );
      } else {
        this.updateFormSection();
        this.cardIsLoading = false;
      }
      // @OLD
      // this.updateFormSection();
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Init components
   *
   * @since 1.0.0
   */


  /**
   * Listen on form values changes
   *
   * @since 1.0.0
   */
  watchFormChanges(): void {
    this.subscriptions.push(this.form.valueChanges.subscribe(
      res => {
        if (res && JSON.stringify(res) === this.previousFormValue) {
          return;
        } else {
          this.previousFormValue = JSON.stringify(res);
          if (this.enableFormModify) {
            this.options.formState = {
              disabled: false,
            };
            this.form.updateValueAndValidity();
          }
          return;
        }
      }
    ));
  }

  /**
   * Listen on input values changes
   *
   * @since 1.0.0
   */
  watchScaleChanges(): void {
    this.mappingIndex = this.mappingIndex.map(
      (item: any): MappingIndex => {
        // Our goal here is to add a event listener to the item we are searching for
        if (
          item.title === "properties.structures.strutture" &&
          Array.isArray(item.content)
        ) {
          item.content.map((subItem: any) => {
            if (
              subItem.key === "fabbricatoAbitativo" &&
              subItem.formConfig &&
              subItem.formConfig.length &&
              subItem.formConfig[0].fieldGroup
            ) {
              const field = subItem.formConfig[0].fieldGroup.find(
                el => el.key === "nscale"
              );
              if (field) {
                field.hooks = {
                  onInit: (field: any): void => {
                    field.formControl.valueChanges
                      .pipe(
                        takeUntil(this.onDestroy$),
                        startWith(field.formControl.value),
                        tap(value => {
                          if (typeof value === "undefined") {
                            value = null;
                          }
                          if (value > field.templateOptions.max) {
                            value = field.templateOptions.max;
                          }
                          if (this.model.scale) {
                            this.model.scale.length = value;
                          }
                          this.model = {
                            ...this.model,
                            nscale: value
                          };
                        })
                      )
                      .subscribe();
                  }
                };
              }
            }
            return subItem;
          });
        }
        return item;
      }
    );
  }

  /**
   * Listen on input values changes
   *
   * @since 1.0.0
   */
  watchBruciatoriChanges(): void {
    this.mappingIndex = this.mappingIndex.map(
      (item: any): MappingIndex => {
        // Our goal here is to add a event listener to the item we are searching for
        if (
          item.title === "properties.structures.riscaldamentoCondizionamento" &&
          Array.isArray(item.content)
        ) {
          item.content.map((subItem: any) => {
            if (
              subItem.key === "centraleTermica" &&
              subItem.formConfig &&
              subItem.formConfig.length &&
              subItem.formConfig[0].fieldGroup
            ) {
              const field = subItem.formConfig[0].fieldGroup.find(
                el => el.key === "numeroBruciatori"
              );
              if (field) {
                field.hooks = {
                  onInit: (field: any): void => {
                    field.formControl.valueChanges
                      .pipe(
                        takeUntil(this.onDestroy$),
                        startWith(field.formControl.value),
                        tap(value => {
                          if (typeof value === "undefined") {
                            value = null;
                          }
                          if (value > field.templateOptions.max) {
                            value = field.templateOptions.max;
                          }
                          this.model.bruciatori.length = value;
                          this.model = {
                            ...this.model,
                            numeroBruciatori: value
                          };
                        })
                      )
                      .subscribe();
                  }
                };
              }
            }
            return subItem;
          });
        }
        return item;
      }
    );
  }

  /**
   * Translate all keys with given labels inside given object and returns it
   *
   * @since 4.0.0
   */
  translateKeys(keys: string[], obj: any): any {
    if (Array.isArray(obj)) {
      obj.forEach((item: MappingIndex) => {
        for (const prop in item) {
          if (item.hasOwnProperty(prop)) {
            if (keys.includes(prop)) {
              item[prop] = this.translateService.instant(item[prop]);
            }
            if (typeof item[prop] === "object") {
              item[prop] = this.translateKeys(keys, item[prop]);
            }
          }
        }
      });
    } else {
      for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          if (keys.includes(prop) && obj[prop]) {
            obj[prop] = this.translateService.instant(obj[prop]);
          }
          if (typeof obj[prop] === "object") {
            obj[prop] = this.translateKeys(keys, obj[prop]);
          }
        }
      }
    }
    return obj;
  }
  // @NEW
  // hooks: {
  //   onInit(field: any) {
  //     field.defaultValue = `Scala ${+field.parent.key + 1}`; // formArray index + 1
  //   },
  // },
  // hooks: {
  //   onInit(field: any) {
  //     field.defaultValue = `Bruciatore ${
  //       +field.parent.key + 1
  //     }`; // formArray index + 1
  //   },
  // },
  translateKeys2(keys: string[], obj: any): any {
    if (Array.isArray(obj)) {
      obj.forEach((item: MappingIndex) => {
        for (const prop in item) {
          // hooks: 
          // 1) numerazione progressiva all'aggiunta delle scale del Fabbricato abitativo
          // 2) numerazione progressiva all'aggiunta dei bruciatori di Centrale termica
          if (prop === "hooks") {
            const propKey = item[prop];
            item[prop] = {
              onInit(field: any) {
                field.defaultValue = `${propKey} ${+field.parent.key + 1}`;
              },
            };
          }
          // hideExpression: 
          // nasconde il corrente campo se il valore del campo indicato soddisfa i criteri indicati da "operator"
          // e diabilita l'obbligatorietà del campo in "templateOptions.required"
          if (prop === "hideExpression" && item[prop]) {
            const propKey = item[prop].key;
            const propCompareValue = item[prop].value;
            if (item[prop].operator === "!==") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                const compareValue = propCompareValue;
                return currentValue !== compareValue;
              }
            } else if (item[prop].operator === "!") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                return !currentValue;
              }
            } else if (item[prop].operator === "!includes") {
              item[prop] = (
                model: any,
                formState: any,
                field: FormlyFieldConfig
              ): boolean => {
                const key = propKey;
                const currentValue = model[key];
                const compareValue = propCompareValue;
                return typeof currentValue === "undefined" || !currentValue.includes(compareValue);
              }
            }
          }
          // expressionProperties:
          // Contatore energia elettrica:
          // disabilita il campo "connessioni" e/o "alimenta un singolo impianto" 
          // usato in "templateOptions.disabled"
          if (prop === "expressionProperties" && item[prop]) {
            let propDisabled = item[prop].disabled;
            if (
              propDisabled !== "formState.disabled" &&
              propDisabled !== "connessioni" &&
              propDisabled !== ""
            ) {
              const key = propDisabled;
              propDisabled = (model: any, formState: any, field: FormlyFieldConfig) => {
                return model[key] || formState.disabled;
              };
            } else if (propDisabled === "connessioni") {
              const key = propDisabled;
              propDisabled = (model: any, formState: any, field: FormlyFieldConfig) => {
                if (model[key] === "undefined") {
                  return false;
                } else if (Array.isArray(model[key]) && model[key].length > 0) {
                  return true;
                }
                return false;
              };
            }
            if (typeof item[prop].required !== "undefined") {
              const propKey = item[prop].required.key;
              const propCompareValue = item[prop].required.value;
              if (item[prop].required.operator === "===") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    const compareValue = propCompareValue;
                    return currentValue === compareValue;
                  }
                }
              } else if (item[prop].required.operator === "!") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    return !currentValue;
                  }
                }
              } else if (item[prop].required.operator === "") {
                item[prop] = {
                  "templateOptions.disabled": propDisabled,
                  "templateOptions.required": (model: any, formState: any, field: FormlyFieldConfig) => {
                    const key = propKey;
                    const currentValue = model[key];
                    return currentValue;
                  }
                }
              }
            } else {
              item[prop] = {
                "templateOptions.disabled": propDisabled,
              }
            }
          }
          // defaultValue:
          // contiene il valore predefinito del campo corrente
          if (prop === "defaultValue" && item[prop]) {
            if (item[prop] === "false") {
              item[prop] = false;
            } else if (item[prop] === "true") {
              item[prop] = true;
            }
          }
          // wrappers:
          // struttura del titolo/campo nel form: orizzontale/verticale  
          // oppure in assenza di "key" e in presenza di "template" => visualizza contenuto di "template"
          if (prop === "wrappers" && item[prop] && Array.isArray(item[prop]) && item[prop].length === 0) {
            item[prop] = "";
          }
          if (item.hasOwnProperty(prop)) {
            if (keys.includes(prop)) {
              if (item["translation"]) {
                item[prop] = item["translation"].it;
              }
            }
            if (typeof item[prop] === "object") {
              item[prop] = this.translateKeys2(keys, item[prop]);
            }
          }
        }
      });
    } else {
      for (const prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          // options:
          // in "templateOptions" e
          // contiene l'elenco dei valori del campo di tipo select (value/translation)
          if (prop === "options" && Array.isArray(obj[prop])) {
            obj[prop].forEach((elem: any) => {
              if (elem.value && elem.value === "true") {
                elem.value = true;
              } else if (elem.value && elem.value === "false") {
                elem.value = false;
              }
            })
          }
          if (keys.includes(prop) && obj[prop]) {
            obj[prop] = this.translateService.instant(obj[prop]);
            if (prop === "label" && obj["translationLabel"]) {
              obj[prop] = obj["translationLabel"].it;
            } else if (prop === "placeholder" && obj["translationPlaceholder"]) {
              obj[prop] = obj["translationPlaceholder"].it;
            }
          }
          if (typeof obj[prop] === "object") {
            obj[prop] = this.translateKeys2(keys, obj[prop]);
          }
        }
      }
    }
    return obj;
  }

  /**
   * Get children (existing values) for requested section
   *
   * @since 1.0.0
   */
  getChildren(key: any): any[] {
    if (typeof this.condoStructure !== "undefined"
      && typeof this.condoStructure[key] !== "undefined") {
      if (key === "anagrafica") {
        return [];
      } else {
        if (Array.isArray(this.condoStructure[key])) {
          let structures = JSON.parse(
            JSON.stringify(this.condoStructure[key])
          );
          structures = structures.filter(el => el.active);
          return structures;
        } else {
          return [];
        }
      }
    }
    return [];
  }

  /**
   * Update form section depending on route params
   *
   * @since 1.0.0
   */
  updateFormSection(): void {
    // Reset model to prevent import previous form data
    if (this.model) {
      for (const property in this.model) {
        delete this.model[property];
      }
    }

    const target = document.getElementById("sideMenu");
    const top = target.offsetHeight;

    window.scrollTo({
      top,
      behavior: "smooth",
    });

    // Reset data and call the new data form
    if (this.form) {
      this.options.formState = {
        disabled: true
      };
    }

    // Add section param to routing, which is used in connections type
    const params = Object.assign({}, this.activatedRoute.snapshot.params, { selected: false });
    params.mappingFormActiveSection = this.formActiveSection;
    this.activatedRoute.snapshot.params = params;

    if (this.formActiveSection) {
      this.formIsLoading = true; // Start form loading

      // BEGIN - Set open status to current mapping index
      const origFormActiveSection = this.formActiveSection.replace(
        this.property.structure,
        ":structureId"
      );

      this.mappingIndex.map((item: MappingIndex) => {
        item.content.map((subItem: MappingIndex) => {
          if (subItem.section === origFormActiveSection) {
            // Get currente element is a Plant
            // delete note field from form
            // if (subItem.formConfig
            //   && Array.isArray(subItem.formConfig)
            //   && subItem.formConfig.length) {
            //   if (subItem.formConfig[0].fieldGroup
            //     && Array.isArray(subItem.formConfig[0].fieldGroup)
            //     && subItem.formConfig[0].fieldGroup.length) {
            //     const index = subItem.formConfig[0].fieldGroup.findIndex((el: any) => {
            //       return el.key === "note";
            //     })
            //     if (index > -1) {
            //       subItem.formConfig[0].fieldGroup.splice(index, 1);
            //     }
            //   }
            // }
            if (
              item.title === "properties.structures.impiantiElettrici" ||
              item.title === "properties.structures.trattamentoAcqua" ||
              item.title ===
              "properties.structures.riscaldamentoCondizionamento" ||
              item.title === "properties.structures.dispositiviAntincendio" ||
              item.title === "properties.structures.ascensoriEMontauto" ||
              item.title === "properties.structures.impiantiElettronici" ||
              item.title === "properties.structures.lineeVita" ||
              item.title === "properties.structures.impiantiSpeciali" ||
              item.title === "properties.structures.impiantiRicreativi"
            ) {
              this.sectionPlant = true;
            } else {
              this.sectionPlant = false;
            }


            item.expanded = true;
            subItem.expanded = true;
            subItem.active = true;
            this.activeItem = subItem;
            if (
              this.formActiveSection &&
              origFormActiveSection !==
              "condo-structures/:structureId/anagrafica"
            ) {
              const children = this.getChildren(subItem.key);
              if (children.length) {
                this.activeSubitem = children.find(
                  (val: any) => val._id === this.formActiveSubitemId
                );

                if (this.activeSubitem && this.activeSubitem.files) {
                  if (this.form.controls.files) {
                    this.form.controls.files.setValue(this.activeSubitem.files);
                  }
                  this.form.updateValueAndValidity();
                }
                if (this.activeSubitem && this.activeSubitem.note) {
                  this.existingNotes = this.activeSubitem.note;
                  this.activeSubitem.note = null;
                }
                this.fields = subItem.formConfig;

                if (this.activeSubitem) {
                  this.model = this.activeSubitem;
                } else {
                  // Add new subitem. Default name from section
                  const tot = children.length + 1;
                  const nome = `${subItem.translation.it} ${tot}`;
                    // this.translateService.instant(subItem.title) + " " + tot;
                  this.model = { nome, files: [], };

                  // Delete defaultValue from field Files, to clear previous upload image
                  if (subItem.formConfig
                    && Array.isArray(subItem.formConfig)
                    && subItem.formConfig.length) {
                    if (subItem.formConfig[0].fieldGroup
                      && Array.isArray(subItem.formConfig[0].fieldGroup)
                      && subItem.formConfig[0].fieldGroup.length) {
                      const index = subItem.formConfig[0].fieldGroup.findIndex((el: any) => {
                        return el.key === "files";
                      })
                      if (index > -1) {
                        if (subItem.formConfig[0].fieldGroup[index].templateOptions
                          && Array.isArray(subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue)
                          && subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue.length) {
                          subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue = [];
                        }
                      }
                    }
                  }
                }
              } else {
                if (this.activeSubitem && this.activeSubitem.note) {
                  this.existingNotes = this.activeSubitem.note;
                  this.activeSubitem.note = null;
                }
                // New first subitem. Default name from section
                this.fields = subItem.formConfig;
                const nome = `${subItem.translation.it} 1`;
                  // this.translateService.instant(subItem.title) + " 1";
                this.model = { nome, files: [], };

                // Delete defaultValue from field Files, to clear previous upload image
                if (subItem.formConfig
                  && Array.isArray(subItem.formConfig)
                  && subItem.formConfig.length) {
                  if (subItem.formConfig[0].fieldGroup
                    && Array.isArray(subItem.formConfig[0].fieldGroup)
                    && subItem.formConfig[0].fieldGroup.length) {
                    const index = subItem.formConfig[0].fieldGroup.findIndex((el: any) => {
                      return el.key === "files";
                    })
                    if (index > -1) {
                      if (subItem.formConfig[0].fieldGroup[index].templateOptions
                        && Array.isArray(subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue)
                        && subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue.length) {
                        subItem.formConfig[0].fieldGroup[index].templateOptions.defaultValue = [];
                      }
                    }
                  }
                }
              }
            } else if (
              subItem.section === "condo-structures/:structureId/anagrafica"
            ) {
              this.activeSubitem = this.condoStructure.anagrafica;
              // Anagrafica: is not completed
              if (typeof this.activeSubitem === "undefined") {
                this.activeSubitem = {
                  active: true
                };
              }
              if (this.activeSubitem && this.activeSubitem.note) {
                this.existingNotes = this.activeSubitem.note;
                this.activeSubitem.note = null;
              }
              this.fields = subItem.formConfig;
              this.model = this.activeSubitem;
              this.formActiveSubitemId = Math.random().toString();
            }
          } else {
            subItem.active = false;
          }
        });
      });

      if (this.form && !this.condoStructure.open) {
        this.options.formState = {
          disabled: true
        };
      }
      // END - Set open status to current mapping index

      setTimeout(() => {
        this.formIsLoading = false;
      }, 300);

    } else {
      // Reset the last item.expanded mappingIndex to close tree from previous structure
      if (Array.isArray(this.mappingIndex) && this.mappingIndex.length) {
        this.mappingIndex.forEach((item: any) => {
          if (item.expanded) {
            item.expanded = false;
            if (Array.isArray(item.content) && item.content.length) {
              item.content.forEach((elem: any) => {
                if (elem.active) {
                  elem.active = false;
                }
              });
            }
          }
        });
      }
    }
  }

  /**
   * Navigate to given section inside the same property detail page
   *
   * @since 1.0.0
   */
  activateSection(rawSection: string): void {
    const target = document.getElementById("sideMenu");
    const top = target.offsetHeight;

    window.scrollTo({
      top,
      behavior: "smooth",
    });

    // Return if we're trying to navigate to 'anagrafica' but
    // we're already in that route
    if (rawSection.endsWith('anagrafica')) {
      if (
        this.activatedRoute.snapshot.params.mappingFormActiveSection
        && this.activatedRoute.snapshot.params.mappingFormActiveSection.endsWith('anagrafica')
      ) {
        return;
      }
    }

    // Reset data and call the new data form
    this.formActiveSubitemId = undefined;

    const controls = this.form.controls;
    for (const key in controls) {
      this.form.removeControl(key);
    }

    const section = rawSection.replace(":structureId", this.property.structure);
    this.formActiveSubitemId = "";
    this.formActiveSection = section;

    // this.router.navigate(
    //   [this.lang, "app", "properties", this.property._id, "mapping", section],
    //   {
    //     replaceUrl: true,
    //     // queryParams: { refresh: new Date().getTime() }
    //   }
    // );
    this.updateFormSection();
  }

  /**
   * Handle panel header open/close
   *
   * @since 1.0.0
   *
   */
  onPanelClicked(item: any, index: number): void {
    item.expanded = !item.expanded;
    this.mappingIndex.forEach((el: MappingIndex, i: number) => {
      if (i !== index) {
        el.expanded = false;
      }
    });

    // Handle scroll to panel
    if (item.expanded) {
      const headerHeight = 60;
      const top = (headerHeight * (index + 1)) - headerHeight;
      window.scrollTo({
        top,
        behavior: "smooth"
      });
    }
  }

  /**
   * Handle form item click and open view/edit popup
   *
   * @since 1.0.0
   */
  onFormItemClick(rawSection: string, item: any): void {
    const target = document.getElementById("sideMenu");
    const top = target.offsetHeight;

    window.scrollTo({
      top,
      behavior: "smooth",
    });

    let itemId = "";
    if (item && item._id) {
      itemId = item._id;
    }

    const section = rawSection.replace(":structureId", this.property.structure);

    if (rawSection !== 'condo-structures/:structureId/anagrafica') {
      this.enableFormModify = false;
    }


    this.formActiveSubitemId = item._id;
    this.formActiveSection = section;
    this.updateFormSection();

  }

  /**
   * Check if given macr-section item is visibile or not depending on
   * structure open status. If structure is open, than this will always returns true
   * If it's false, this will check if at least one sub-section has one or more
   * values. If so, return true, otherwise return false;
   *
   * @since 1.0.0
   */
  checkVisibility(item: any): boolean {
    if (!this.mappaturaOpen) {
      if (item.content && item.content.length) {
        // Let's make anagrafica section ALWAYS visible
        if (item.content[0].key === "anagrafica") {
          return true;
        }
        return item.content.some(
          (subitem: MappingIndex) => this.getChildren(subitem.key).length
        );
      }
      return false;
    }
    return true;
  }

  /**
   * Open note dialog
   *
   * @since 1.0.0
   */
  onNoteDialog(): void {
    const dialogRef = this.dialog.open(
      PropertyDetailMappingNoteDialogComponent,
      {
        data: {
          modalTitle: this.translateService.instant(
            "properties.structures.notes"
          ),
          existingNotes: this.existingNotes,
          condoStructureId: this.condoStructure._id,
          structureId: this.model._id,
          structureName: this.model.nome,
        },
        width: "500px"
      }
    );
  }
  
  /**
   * Handle click to show document notes
   *
   * @since 1.0.0
   */
    onViewDocumentNotes(): void {
    const note = [];

    if (this.condoStructure) {
      for (const key in this.condoStructure) {
        if (
          Array.isArray(this.condoStructure[key]) &&
          this.condoStructure[key].length
        ) {

          let structureTranslated = '';
          let find = false;
          const structureTemplate = JSON.parse(JSON.stringify(this.condoStructureTemplate));
          if (structureTemplate && Array.isArray(structureTemplate)) {
            for (const level1 of structureTemplate) {
              if (level1.content && Array.isArray(level1.content)) {
                for (const level2 of level1.content) {
                  // if (level2.title === `properties.structures.${key}`) {
                  if (level2.key === key) {
                    structureTranslated = level2.translation[this.lang];
                    find = true;
                    break;
                  }
                }
              }
              if (find) {
                break;
              }
            }
          }
          
          this.condoStructure[key].forEach((el: any, index: number) => {
            if (el.active && el.note && Array.isArray(el.note) && el.note.length) {
              el.note.forEach((subEl: any, indexNote: number) => {
                note.push({
                  createdAt: subEl.createdAt,
                  createdBy:
                    subEl.createdBy.firstName + " " + subEl.createdBy.lastName,
                  text: subEl.text,
                  name: el.nome ? el.nome : "",
                  // structure: this.translateService.instant(
                  //   "properties.structures." + key
                  // ),
                  structure: structureTranslated,
                  structureName: key,
                  structurePosition: index,
                  structureId: el._id,
                  notePosition: indexNote,
                  noteId: subEl._id,
                });
              });
            }
          });
        }
      }
    }

    const noteDialogRef = this.dialog.open(
      PropertyDetailDocumentNoteDialogComponent,
      {
        data: {
          modalTitle: this.translateService.instant(
            "properties.documentsPage.noteList"
          ),
          condoStructureId: this.condoStructure._id,
          notes: note,
        },
        minWidth: "50%",
      }
    );
  }
}